import { Audience } from '@/models/audiences';
import { Trait } from '@/models/traits';
import { FormikProps } from 'formik';
import { FC, PropsWithChildren, RefObject } from 'react';
import { AudienceStepId } from '../constants';
import { AudienceConfirmation } from './AudienceConfirmation';
import { AudienceForm, AudienceFormModel } from './AudienceForm';
import { ExpressionContainer } from './expressions/ExpressionContainer';
import { AudienceSyncContainer, AudienceSyncFormModel } from './sync/AudienceSyncContainer';
import { PageLayout } from '@/global/components';
import { CreateStepper } from '@/lib/Steppers';
import { CreateStepArgs } from '@/lib/Steppers/CreateStepper/types';
import { ModalHeader } from '@/components/modals';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/reducers';

type AudienceModalContentProps = {
  createFormRef: RefObject<FormikProps<AudienceFormModel>>;
  onCreateFormSubmit: (form: AudienceFormModel) => void;
  syncFormRef: RefObject<FormikProps<AudienceSyncFormModel>>;
  steps: CreateStepArgs<AudienceStepId>[];
  stepId: AudienceStepId;
  currentStepIndex: number;
  traits?: Trait[];
  audience?: Partial<Audience>;
  onSuccess: (audience: Audience) => void;
  closeModal: VoidFunction;
  readOnly?: boolean;
} & PropsWithChildren;

export const AudienceModalContent: FC<AudienceModalContentProps> = ({
  createFormRef,
  onCreateFormSubmit,
  stepId,
  steps,
  currentStepIndex,
  syncFormRef,
  traits,
  audience,
  onSuccess,
  closeModal,
  readOnly,
  children,
}) => {
  const { t } = useTranslation('audience_edit');

  const stashedAudience = useAppSelector((state) => state.audienceEdit.audience);

  const handleClose = () => {
    onSuccess(stashedAudience as Audience);
    closeModal();
  };

  const stepTemplate = () => {
    switch (stepId) {
      case 'nameAndMetadata':
        return <AudienceForm formRef={createFormRef} onSubmit={onCreateFormSubmit} traits={traits} audience={audience} readOnly={readOnly} />;
      case 'expression':
        return <ExpressionContainer />;
      case 'sync':
        return <AudienceSyncContainer formRef={syncFormRef} />;
      case 'confirmation':
        return (
          <AudienceConfirmation
            primaryButton={{
              text: 'COMMON.close',
              onClick: handleClose,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ModalHeader classNames={{ wrapper: 'border-b pb-4 py-4' }} title={t(`title`)} closeModal={closeModal} />
      <PageLayout>
        <div className='flex h-full flex-col text-start' data-testid='audienceEdit'>
          <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />
          <div className='mt-6 rounded-lg'>{stepTemplate()}</div>
          {children}
        </div>
      </PageLayout>
    </>
  );
};
